import React, { useEffect } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import propTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { countryCodes, stateCodes, provinceCodes } from '../../Utils/Consts';
import { StyledButton } from '../../Components/Generics.styled';

const AboutYourBusinessFormContainer = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
`;

const FormSection = styled.div`
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: column;
`;
const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 900px;
    justify-content: space-evenly;
    height: 80px;
`;

const WelcomeText = styled.div`
    text-align: center;
    letter-spacing: 0px;
    color: #343a40;
    opacity: 1;
    white-space: nowrap;
`;

const HelpText = styled(WelcomeText)`
    font-size: 21px;
`;

const NavigationSection = styled.div`
    display: flex;
    width: 140px;
    justify-content: space-between;
`;

const NavButtonNext = styled(Button)`
    background: #2e69fe 0% 0% no-repeat padding-box !important;
    box-shadow: 0px 3px 3px #00000061;
    border-radius: 4px;
    opacity: 1;
    width: 50px;
    top: 20px;
    height: 50px;

    color: #fafbf7;
    &.MuiButton-contained.Mui-disabled {
        color: #fafbf7;
    }
    :disabled {
        background: #9b9b9d 0% 0% no-repeat padding-box !important;
    }
`;

const StyledTextField = styled(TextField)`
    width: 350px;
    top: 16px;
`;

const StyledDropdown = styled.div`
    margin-top: 16px;
`;

const SwitchSection = styled.div`
    width: 400px;
`;

const SwitchLabel = styled.div`
    letter-spacing: 0px;
    color: #000000;
    width: 200px;
`;
const SwitchSubLabel = styled.div`
    font: italic normal normal 16px/19px Roboto;
    letter-spacing: 0px;
    color: #6f6f6f;
    width: 300px;
`;

const RadioSection = styled.div`
    .MuiFormGroup-root {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
    }
`;

const TellUsAboutYourBusinessForm = ({
    setAboutYourBusinessData,
    aboutYourBusinessData,
    history,
    planTypeData,
}) => {
    const handleNavNextClick = () => history.push('/aboutYourBusiness/view');

    const {
        companyName,
        companyWebsite,
        firstName,
        lastName,
        title,
        primaryPhone,
        mobilePhone,
        email,
        streetAddress,
        city,
        state,
        country,
        zipCode,
        accountType,
        planTypeId,
        planTypeName,
        planTypePrice,
        planTypeDuration,
    } = { ...aboutYourBusinessData };

    const isEssentialsPlan = planTypeName === 'Essentials';

    const disableNextButton =
        companyName === '' ||
        companyWebsite === '' ||
        firstName === '' ||
        lastName === '' ||
        primaryPhone === '' ||
        title === '' ||
        mobilePhone === '' ||
        streetAddress === '' ||
        city === '' ||
        state === '' ||
        country === '' ||
        zipCode === '' ||
        accountType === '' ||
        email === '' ||
        planTypeId === '' ||
        planTypeId === null;

    const calculateRadioValues = () => {
        if (accountType === 'Advertiser') {
            return 'No';
        }
        if (accountType === 'Agency') {
            return 'Yes';
        }
        if (isEssentialsPlan) {
            return 'No';
        }
        return '';
    };

    useEffect(() => {
        if (planTypeId === null) {
            history.push('/aboutYourBusiness/plan');
        }
        if (isEssentialsPlan) {
            setAboutYourBusinessData({ ...aboutYourBusinessData, accountType: 'advertiser' });
        }
    }, [planTypeId, isEssentialsPlan]);

    return (
        <div>
            <WelcomeText>Tell us about your business</WelcomeText>
            <br />
            <HelpText>
                Provide more information to those customers who are interested in what you sell.
            </HelpText>
            <br />
            <AboutYourBusinessFormContainer>
                <FormSection>
                    <RowContainer>
                        <StyledTextField
                            id="company-name"
                            label="Company Name"
                            defaultValue={companyName}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    companyName: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={companyName === ''}
                        />
                        <StyledTextField
                            id="company-website"
                            label="Company Website"
                            defaultValue={companyWebsite}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    companyWebsite: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={companyWebsite === ''}
                        />
                    </RowContainer>
                    <RowContainer>
                        <StyledTextField
                            id="first-name"
                            label="First Name"
                            defaultValue={firstName}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    firstName: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={firstName === ''}
                        />
                        <StyledTextField
                            id="last-name"
                            label="Last Name"
                            defaultValue={lastName}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    lastName: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={lastName === ''}
                        />
                    </RowContainer>
                    <RowContainer>
                        <StyledTextField
                            id="title"
                            label="Your Title"
                            defaultValue={title}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    title: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={title === ''}
                        />
                        <StyledTextField
                            id="planType"
                            label="Plan Type"
                            value={
                                planTypeName === null
                                    ? 'You must select a valid Plan Type'
                                    : `${planTypeName === 'ProPlus' ? 'Pro +' : planTypeName} - ${
                                          planTypeDuration === 'monthly'
                                              ? `$${planTypePrice} billed monthly`
                                              : `$${planTypePrice} billed anually`
                                      }`
                            }
                            variant="outlined"
                            disabled
                        />
                    </RowContainer>
                    <RowContainer>
                        <StyledTextField
                            id="primary-phone"
                            label="Primary Phone"
                            defaultValue={primaryPhone}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    primaryPhone: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={primaryPhone === ''}
                        />
                        <StyledTextField
                            id="mobile-phone"
                            label="Mobile Phone"
                            defaultValue={mobilePhone}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    mobilePhone: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={mobilePhone === ''}
                        />
                    </RowContainer>
                    <RowContainer>
                        <StyledTextField
                            style={{ width: '770px' }}
                            id="email-address"
                            label="Email Address"
                            defaultValue={email}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    email: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={email === ''}
                        />
                    </RowContainer>
                    <RowContainer>
                        <StyledTextField
                            style={{ width: '770px' }}
                            id="street-address"
                            label="Street Address"
                            defaultValue={streetAddress}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    streetAddress: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={streetAddress === ''}
                        />
                    </RowContainer>
                    <RowContainer>
                        <StyledDropdown>
                            <FormControl required error={country === ''} variant="outlined">
                                <InputLabel id="country-dropdown">Country</InputLabel>
                                <Select
                                    style={{ width: '180px' }}
                                    labelId="country-dropdown"
                                    id="country-dropdown"
                                    value={country}
                                    onChange={(e) =>
                                        setAboutYourBusinessData({
                                            ...aboutYourBusinessData,
                                            country: e.target.value,
                                            state: '',
                                        })
                                    }
                                    label="country"
                                >
                                    {countryCodes.map((item, idx) => (
                                        <MenuItem key={idx} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </StyledDropdown>
                        <StyledTextField
                            style={{ width: '200px' }}
                            id="city"
                            label="City"
                            defaultValue={city}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    city: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={city === ''}
                        />
                        <StyledDropdown>
                            <FormControl
                                disabled={country === ''}
                                required
                                error={country !== '' && state === ''}
                                variant="outlined"
                            >
                                <InputLabel id="state-or-province">
                                    {country === 'USA' ? 'State' : 'Province'}
                                </InputLabel>
                                <Select
                                    style={{ width: '120px' }}
                                    labelId="state-or-province"
                                    id="state-or-province"
                                    value={state}
                                    onChange={(e) =>
                                        setAboutYourBusinessData({
                                            ...aboutYourBusinessData,
                                            state: e.target.value,
                                        })
                                    }
                                    label="state"
                                >
                                    {country === 'USA'
                                        ? stateCodes.map((item, idx) => (
                                              <MenuItem key={idx} value={item}>
                                                  {item}
                                              </MenuItem>
                                          ))
                                        : provinceCodes.map((item, idx) => (
                                              <MenuItem key={idx} value={item}>
                                                  {item}
                                              </MenuItem>
                                          ))}
                                </Select>
                            </FormControl>
                        </StyledDropdown>
                        <StyledTextField
                            style={{ width: '93px' }}
                            id="zipcode"
                            label="Zipcode"
                            defaultValue={zipCode}
                            onChange={(e) =>
                                setAboutYourBusinessData({
                                    ...aboutYourBusinessData,
                                    zipCode: e.target.value,
                                })
                            }
                            variant="outlined"
                            required
                            error={zipCode === ''}
                        />
                    </RowContainer>

                    <RowContainer>
                        <SwitchSection>
                            <SwitchLabel>*Are you an agency or have multiple brands?</SwitchLabel>
                            <SwitchSubLabel>
                                Will you be creating campaigns for other entities besides your
                                business?
                            </SwitchSubLabel>
                        </SwitchSection>
                        <RadioSection>
                            <FormControl disabled={isEssentialsPlan} component="fieldset">
                                <RadioGroup
                                    aria-label="isAgency"
                                    name="isAgency"
                                    value={calculateRadioValues()}
                                    onChange={(e) =>
                                        setAboutYourBusinessData({
                                            ...aboutYourBusinessData,
                                            accountType:
                                                e.target.value === 'No' ? 'Advertiser' : 'Agency',
                                        })
                                    }
                                >
                                    <FormControlLabel
                                        value="Yes"
                                        control={
                                            <Radio
                                                style={{
                                                    color: accountType === '' ? 'red' : '#3F51B5',
                                                }}
                                            />
                                        }
                                        label="Yes"
                                    />
                                    <FormControlLabel
                                        value="No"
                                        control={
                                            <Radio
                                                style={{
                                                    color: accountType === '' ? 'red' : '#3F51B5',
                                                }}
                                            />
                                        }
                                        label="No"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </RadioSection>
                        <NavigationSection>
                            <StyledButton
                                // disabled={disableNextButton}
                                onClick={() => history.push('/aboutYourBusiness/plan')}
                                variant="contained"
                                color="primary"
                            >
                                Back
                            </StyledButton>
                            <StyledButton
                                disabled={disableNextButton}
                                onClick={() => handleNavNextClick()}
                                variant="contained"
                                color="primary"
                            >
                                Next
                            </StyledButton>
                        </NavigationSection>
                    </RowContainer>
                </FormSection>
            </AboutYourBusinessFormContainer>

            <br />
        </div>
    );
};
TellUsAboutYourBusinessForm.propTypes = {
    setAboutYourBusinessData: propTypes.func,
    aboutYourBusinessData: propTypes.shape({}),
    planTypeData: propTypes.array,
    history: propTypes.shape({
        push: propTypes.func,
    }),
};

TellUsAboutYourBusinessForm.defaultProps = {
    setAboutYourBusinessData: () => {},
    aboutYourBusinessData: {},
    history: {},
};
export default TellUsAboutYourBusinessForm;
